// "My business": every current fact, where it came from, and an "is this
// right?" on anything not yet confirmed. Edits go back as customer-confirmed
// facts (source: dashboard, confirm: true), which is what the contract asks.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Pencil } from 'lucide-react';
import { ok } from '@cover/core/api/client.ts';
import type { BusinessProfile, Fact, FactField, FactValue } from '@cover/core/api/types.ts';
import { SOURCE_LABEL, date, plural } from '@cover/core/format.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody, CardHeader } from '@cover/core/ui/Card.tsx';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { FactValueInput, canEdit, toInitial, validate } from '@cover/core/ui/FactValueInput.tsx';
import { Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, useFields, useProfile, useProposedFacts } from '../api.ts';
import { PageTitle } from '../Layout.tsx';
import { useBusinessId } from '../session.tsx';

const AREAS: [FactField['area'], string][] = [
  ['identity', 'Your business'],
  ['trade', 'The work you do'],
  ['turnover', 'Money'],
  ['people', 'People'],
  ['work', 'Where and how you work'],
  ['equipment', 'Equipment'],
  ['vehicles', 'Vehicles'],
  ['cover', 'Your other insurance'],
  ['claims', 'Claims history'],
  ['contracts', 'Contracts'],
];

function useInvalidateProfile() {
  const qc = useQueryClient();
  const b = useBusinessId();
  return () => Promise.all([qc.invalidateQueries({ queryKey: keys.profile(b) }), qc.invalidateQueries({ queryKey: keys.facts(b) })]);
}

export function BusinessPage() {
  const profile = useProfile();
  const fields = useFields();
  const proposed = useProposedFacts();
  const b = useBusinessId();
  const toast = useToast();
  const refresh = useInvalidateProfile();
  const [editing, setEditing] = useState<{ def: FactField; fact?: Fact } | null>(null);

  const confirm = useMutation({
    mutationFn: (factIds: string[]) => ok(api.POST('/businesses/{businessId}/confirmations', { params: { path: { businessId: b } }, body: { factIds, channel: 'dashboard' } })),
    onSuccess: (_d, ids) => {
      toast.ok(ids.length > 1 ? 'Thanks, all confirmed.' : 'Thanks, confirmed.');
      return refresh();
    },
    onError: (e) => {
      toast.fail(e);
      return refresh();
    },
  });

  return (
    <>
      <PageTitle lead="This is what we know about your business. If anything’s wrong, change it here and we’ll use your answer.">My business</PageTitle>
      <Load q={profile}>
        {(p) => {
          const defs = fields.data ?? new Map<string, FactField>();
          const unconfirmed = p.facts.filter((f) => f.status === 'unconfirmed');
          const missing = [...new Set(p.gaps.flatMap((g) => g.missing))].map((k) => defs.get(k)).filter((d): d is FactField => !!d);
          return (
            <div className="space-y-5">
              {p.gaps.length > 0 && <Readiness profile={p} />}

              {unconfirmed.length > 0 && (
                <Card className="border-info-fg/30">
                  <CardHeader title="Please check" description="We picked these up from your messages, photos and connections. We only use them once you’ve said they’re right." />
                  <CardBody className="pt-1">
                    <ul className="divide-y divide-line">
                      {unconfirmed.map((f) => (
                        <li key={f.id} className="py-3">
                          <FactRow fact={f} def={defs.get(f.field)} onEdit={() => defs.get(f.field) && setEditing({ def: defs.get(f.field)!, fact: f })} onConfirm={() => confirm.mutate([f.id])} confirming={confirm.isPending && confirm.variables?.includes(f.id)} />
                        </li>
                      ))}
                    </ul>
                    {unconfirmed.length > 1 && (
                      <Button className="mt-2" variant="secondary" busy={confirm.isPending && confirm.variables?.length === unconfirmed.length} onClick={() => confirm.mutate(unconfirmed.map((f) => f.id))}>
                        They’re all right
                      </Button>
                    )}
                  </CardBody>
                </Card>
              )}

              {missing.length > 0 && (
                <Card>
                  <CardHeader title="Still to tell us" description="We need these before we can quote." />
                  <CardBody>
                    <ul className="divide-y divide-line">
                      {missing.map((def) => (
                        <li key={def.field} className="flex items-center justify-between gap-3 py-3">
                          <span>{def.question ?? def.label}</span>
                          <Button size="sm" variant="secondary" onClick={() => setEditing({ def })}>
                            Add
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </CardBody>
                </Card>
              )}

              {AREAS.map(([area, title]) => {
                const facts = p.facts.filter((f) => f.status !== 'unconfirmed' && defs.get(f.field)?.area === area);
                if (!facts.length) return null;
                return (
                  <Card key={area}>
                    <CardHeader title={title} />
                    <CardBody className="pt-1">
                      <ul className="divide-y divide-line">
                        {facts.map((f) => {
                          const def = defs.get(f.field);
                          const pending = proposed.data?.find((x) => x.field === f.field);
                          return (
                            <li key={f.id} className="py-3">
                              <FactRow fact={f} def={def} pending={pending} onEdit={() => def && setEditing({ def, fact: f })} onConfirm={() => confirm.mutate([f.id])} confirming={confirm.isPending && confirm.variables?.includes(f.id)} />
                            </li>
                          );
                        })}
                      </ul>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          );
        }}
      </Load>
      {editing && <EditFact def={editing.def} fact={editing.fact} onClose={() => setEditing(null)} />}
    </>
  );
}

/** One line per product we could quote: ready, or how many answers are still to come. */
function Readiness({ profile: p }: { profile: BusinessProfile }) {
  const brand = useBrand();
  return (
    <Card>
      <CardHeader title="Ready for a quote?" description="We only quote from details you’ve confirmed." />
      <CardBody className="pt-2">
        <ul className="divide-y divide-line">
          {p.gaps.map((g) => {
            const name = brand.products.find((x) => x.id === g.product)?.name ?? p.needs.find((n) => n.product === g.product)?.productName ?? g.product;
            const left = g.missing.length + g.unconfirmed.length;
            return (
              <li key={g.product} className="flex items-center justify-between gap-3 py-2.5">
                <span className="font-medium">{name}</span>
                {g.readyToQuote ? <Badge tone="ok">Ready</Badge> : <Badge tone="info">{plural(left, 'answer')} to go</Badge>}
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}

function FactRow({ fact, def, pending, onEdit, onConfirm, confirming }: { fact: Fact; def?: FactField; pending?: Fact; onEdit: () => void; onConfirm: () => void; confirming?: boolean }) {
  const needsCheck = fact.status === 'unconfirmed';
  return (
    <div className="space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-mid">{def?.label ?? fact.field}</p>
          <p className="font-semibold">{fact.display ?? String(fact.value)}</p>
        </div>
        {!needsCheck && def && canEdit(def, fact.value) && (
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Change ${def?.label ?? fact.field}`}>
            <Pencil className="h-4 w-4" aria-hidden /> Change
          </Button>
        )}
      </div>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-mid">
        <span>
          {SOURCE_LABEL[fact.source]}
          {fact.evidenceRef?.excerpt && <> · “{fact.evidenceRef.excerpt}”</>}
        </span>
        {fact.status === 'confirmed' && (
          <span className="inline-flex items-center gap-1 text-ok-fg">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            {fact.confirmedBy === 'staff' ? 'Checked by our team' : 'Confirmed'}
            {fact.confirmedAt && ` ${date(fact.confirmedAt)}`}
          </span>
        )}
      </p>
      {pending && (
        <p className="rounded-md bg-warn-bg px-3 py-2 text-sm text-warn-fg">
          We’ve also seen {pending.display} ({SOURCE_LABEL[pending.source].replace(/^From /, 'from ').replace(/^You /, 'you ')}). Our team is checking which is right; nothing changes until they do.
        </p>
      )}
      {needsCheck && (
        <div className="rounded-md bg-info-bg/50 p-3">
          <p className="mb-2 font-semibold" id={`q-${fact.id}`}>
            Is this right?
          </p>
          <div className="flex gap-2" role="group" aria-labelledby={`q-${fact.id}`}>
            <Button size="sm" busy={confirming} onClick={onConfirm}>
              Yes, that’s right
            </Button>
            {def && canEdit(def, fact.value) ? (
              <Button size="sm" variant="secondary" onClick={onEdit}>
                No, change it
              </Button>
            ) : (
              <WrongInChat label={def?.label ?? fact.field} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** For structured facts the dashboard can't edit, the fix happens in chat. */
function WrongInChat({ label }: { label: string }) {
  const brand = useBrand();
  const wa = whatsappLink(brand, `My ${label.toLowerCase()} isn’t right: `);
  return wa ? (
    <Button asChild size="sm" variant="secondary">
      <a href={wa} className="text-ink no-underline hover:no-underline">
        No, tell us in WhatsApp
      </a>
    </Button>
  ) : null;
}

function EditFact({ def, fact, onClose }: { def: FactField; fact?: Fact; onClose: () => void }) {
  const b = useBusinessId();
  const toast = useToast();
  const refresh = useInvalidateProfile();
  const [value, setValue] = useState<FactValue | null>(toInitial(def, fact?.value));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (v: FactValue) =>
      ok(
        api.POST('/businesses/{businessId}/facts', {
          params: { path: { businessId: b } },
          body: { facts: [{ field: def.field, value: v, source: 'dashboard', confidence: 1 }], confirm: true },
        }),
      ),
    onSuccess: async () => {
      await refresh();
      toast.ok(`${def.label} updated.`);
      onClose();
    },
    onError: (e) => toast.fail(e),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={fact ? `Change ${def.label.toLowerCase()}` : def.label} description={def.question}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          const err = validate(def, value);
          setError(err);
          if (!err) save.mutate(value as FactValue);
        }}
      >
        <FactValueInput def={def} value={value} onChange={setValue} error={error} />
        <p className="text-sm text-mid">Saving confirms this answer is right. We’ll use it for quotes and claims.</p>
        <div className="flex gap-2">
          <Button type="submit" busy={save.isPending}>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
